import { z } from 'zod';

import { SchemaValidationFailedError } from '@kilat/shared';
import { callFunction } from './_lazy.js';

/**
 * Typed wrapper around the public agent-lookup callable. Used by:
 *   - /signup with `?ref=XXX-XXXX` → fetch the agent's display name so the
 *     UI can show "Introduced by …" before submission.
 *   - /a/:agentCode → render the agent's name on their printable QR card.
 *
 * The callable returns a tiny sanitised payload — only name + code +
 * brand id; no PII. Errors are surface-grade:
 *   - 'not-found'           → either the code is wrong or it belongs to a
 *                             different platform brand. UI should treat both
 *                             the same: clear the prefill, show a soft hint,
 *                             let the operator type a different code or
 *                             proceed without one.
 *   - 'failed-precondition' → agent exists but is suspended/terminated.
 *                             Show "this code isn't active right now".
 */

export const GetAgentByCodeInputSchema = z.object({
  code: z.string().min(3).max(20),
  originHost: z.string().min(1),
});
export type GetAgentByCodeInput = z.infer<typeof GetAgentByCodeInputSchema>;

export const GetAgentByCodeOutputSchema = z.object({
  code: z.string(),
  name: z.string(),
  platformBrandId: z.string(),
  status: z.string(),
});
export type GetAgentByCodeOutput = z.infer<typeof GetAgentByCodeOutputSchema>;

export async function callGetAgentByCode(
  input: GetAgentByCodeInput,
): Promise<GetAgentByCodeOutput> {
  const parsed = GetAgentByCodeInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('getAgentByCode.input', parsed.error.flatten());
  }
  const result = await callFunction<GetAgentByCodeInput, GetAgentByCodeOutput>(
    'getAgentByCode',
    parsed.data,
  );
  const out = GetAgentByCodeOutputSchema.safeParse(result);
  if (!out.success) {
    throw new SchemaValidationFailedError('getAgentByCode.output', out.error.flatten());
  }
  return out.data;
}
